import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useClientes } from '../../context/ClientesContext';
import { useTaller3D } from '../../context/Taller3DContext';
import { Cliente } from '../../types';
import { formatearImporte } from '../../utils/calculo3d';
import {
  Plus, Search, Users, Building2, User as UserIcon, Mail, Phone,
  Edit2, Trash2, ChevronRight, EyeOff,
} from 'lucide-react';
import { ModalCliente } from '../../components/clientes/ModalCliente';
import { useConfirmar } from '../../context/ConfirmarContext';

export const Clientes: React.FC = () => {
  const confirmar = useConfirmar();
  const { clientes, loadingClientes, eliminarCliente, actualizarCliente } = useClientes();
  const { presupuestos3D } = useTaller3D();

  const [busqueda, setBusqueda] = useState('');
  const [verInactivos, setVerInactivos] = useState(false);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [clienteEditar, setClienteEditar] = useState<Cliente | undefined>();

  // Cuántos presupuestos y cuánto facturado tiene cada cliente
  const actividad = useMemo(() => {
    const mapa: Record<string, { presupuestos: number; aceptado: number }> = {};
    presupuestos3D.forEach(p => {
      if (!p.clienteId) return;
      const a = mapa[p.clienteId] ?? { presupuestos: 0, aceptado: 0 };
      a.presupuestos += 1;
      if (p.estado === 'aceptado') a.aceptado += p.resumen?.precioTotalConIva ?? 0;
      mapa[p.clienteId] = a;
    });
    return mapa;
  }, [presupuestos3D]);

  const lista = useMemo(() => {
    const t = busqueda.trim().toLowerCase();
    return clientes
      .filter(c => (verInactivos ? true : c.activo))
      .filter(c => !t || [c.nombre, c.email, c.telefono, c.nif, c.poblacion]
        .some(v => v?.toLowerCase().includes(t)));
  }, [clientes, busqueda, verInactivos]);

  const abrirNuevo = () => { setClienteEditar(undefined); setModalAbierto(true); };
  const abrirEditar = (c: Cliente) => { setClienteEditar(c); setModalAbierto(true); };

  const borrar = async (c: Cliente) => {
    const usos = actividad[c.id]?.presupuestos ?? 0;
    if (usos > 0) {
      await confirmar({
        titulo: `«${c.nombre}» tiene ${usos} presupuesto${usos === 1 ? '' : 's'} asociado${usos === 1 ? '' : 's'}`,
        mensaje: 'Borrarlo dejaría esos presupuestos sin cliente. Márcalo como inactivo si solo quieres ocultarlo.',
        accion: 'Entendido',
        soloAviso: true,
      });
      return;
    }
    if (!(await confirmar({ titulo: `¿Borrar a «${c.nombre}»?`, mensaje: 'No se puede deshacer.', accion: 'Borrar' }))) return;
    await eliminarCliente(c.id);
  };

  if (loadingClientes) {
    return <div className="max-w-5xl mx-auto px-4 py-10 text-gray-500 dark:text-gray-400">Cargando…</div>;
  }

  const inactivos = clientes.filter(c => !c.activo).length;

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">

      <header className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Clientes</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
            {clientes.filter(c => c.activo).length} activo{clientes.filter(c => c.activo).length === 1 ? '' : 's'}
            {inactivos > 0 && ` · ${inactivos} inactivo${inactivos === 1 ? '' : 's'}`}
          </p>
        </div>
        <button onClick={abrirNuevo}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg
                     bg-purple-600 hover:bg-purple-700 text-white transition">
          <Plus className="w-4 h-4" /> Nuevo cliente
        </button>
      </header>

      {clientes.length > 0 && (
        <div className="flex flex-wrap gap-2 items-center mb-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="search" value={busqueda} onChange={e => setBusqueda(e.target.value)}
              placeholder="Buscar por nombre, email, NIF o población…"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600
                         bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm
                         focus:outline-none focus:ring-2 focus:ring-purple-500" />
          </div>
          {inactivos > 0 && (
            <button onClick={() => setVerInactivos(v => !v)}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg border transition ${
                verInactivos
                  ? 'bg-gray-900 dark:bg-purple-600 border-gray-900 dark:border-purple-600 text-white'
                  : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'
              }`}>
              <EyeOff className="w-3.5 h-3.5" /> Ver inactivos
            </button>
          )}
        </div>
      )}

      {lista.length === 0 ? (
        <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-xl border border-dashed border-gray-300 dark:border-gray-700">
          <Users className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
          <p className="text-gray-600 dark:text-gray-400 font-medium">
            {clientes.length === 0
              ? 'Todavía no tienes clientes.'
              : 'Ningún cliente coincide con la búsqueda.'}
          </p>
          {clientes.length === 0 && (
            <>
              <p className="text-sm text-gray-500 dark:text-gray-500 mt-1 max-w-sm mx-auto">
                Al crear uno podrás asignarlo a tus presupuestos 3D y llevar su historial.
              </p>
              <button onClick={abrirNuevo}
                className="inline-flex items-center gap-1.5 mt-4 px-4 py-2 text-sm font-semibold rounded-lg
                           bg-purple-600 hover:bg-purple-700 text-white transition">
                <Plus className="w-4 h-4" /> Crear el primero
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {lista.map(c => {
            const act = actividad[c.id];
            return (
              <div key={c.id}
                className={`bg-white dark:bg-gray-800 rounded-xl border p-4 transition
                            hover:border-gray-300 dark:hover:border-gray-600 ${
                  c.activo
                    ? 'border-gray-200 dark:border-gray-700'
                    : 'border-dashed border-gray-300 dark:border-gray-700 opacity-70'
                }`}>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 shrink-0 rounded-lg bg-purple-50 dark:bg-purple-900/30
                                  flex items-center justify-center">
                    {c.tipo === 'empresa'
                      ? <Building2 className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                      : <UserIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />}
                  </div>

                  <Link to={`/clientes/${c.id}`} className="min-w-0 flex-1 group">
                    <h2 className="font-bold text-gray-900 dark:text-white truncate
                                   group-hover:text-purple-600 dark:group-hover:text-purple-400 transition">
                      {c.nombre}
                      {!c.activo && (
                        <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide
                                         text-gray-500 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">
                          Inactivo
                        </span>
                      )}
                    </h2>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {c.nif && <span>{c.nif}</span>}
                      {c.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</span>}
                      {c.telefono && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{c.telefono}</span>}
                      {c.poblacion && <span>{c.poblacion}</span>}
                    </div>
                    {act && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {act.presupuestos} presupuesto{act.presupuestos === 1 ? '' : 's'}
                        {act.aceptado > 0 && ` · ${formatearImporte(act.aceptado, '€')} aceptados`}
                      </p>
                    )}
                  </Link>

                  <div className="flex items-center gap-0.5 shrink-0">
                    <button onClick={() => actualizarCliente(c.id, { activo: !c.activo })}
                      title={c.activo ? 'Marcar como inactivo' : 'Reactivar'}
                      className="p-2 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200
                                 hover:bg-gray-100 dark:hover:bg-gray-700 transition">
                      <EyeOff className="w-4 h-4" />
                    </button>
                    <button onClick={() => abrirEditar(c)} title="Editar"
                      className="p-2 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200
                                 hover:bg-gray-100 dark:hover:bg-gray-700 transition">
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => borrar(c)} title="Borrar"
                      className="p-2 rounded-lg text-gray-400 hover:text-red-600
                                 hover:bg-red-50 dark:hover:bg-red-900/20 transition">
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <Link to={`/clientes/${c.id}`} title="Abrir ficha"
                      className="p-2 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200
                                 hover:bg-gray-100 dark:hover:bg-gray-700 transition">
                      <ChevronRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modalAbierto && (
        <ModalCliente cliente={clienteEditar} onClose={() => setModalAbierto(false)} />
      )}
    </div>
  );
};
